import React, { useState } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import {
  Brain, Sparkles, BookOpen, Target, Lightbulb, ClipboardList,
  CheckCircle, AlertTriangle, TrendingUp, Clock, Loader2,
  FileText, Rocket, Heart, Award, Zap
} from 'lucide-react';

// ============================================
// BLOCO 4: BOLETIM INTELIGENTE
// ============================================

export function BoletimInteligente({ studentId, studentName, onClose }) {
  const [loading, setLoading] = useState(false);
  const [boletim, setBoletim] = useState(null);
  const [trimester, setTrimester] = useState('');
  
  const generateBoletim = async () => {
    setLoading(true);
    try {
      const res = await api.post('/dnacore/v2/ai/boletim-inteligente', {
        student_id: studentId,
        trimester: trimester || null,
        include_recommendations: true
      });
      
      setBoletim(res.data.boletim);
      toast.success('Boletim gerado com sucesso!');
    } catch (error) {
      console.error('Erro ao gerar boletim:', error);
      toast.error('Erro ao gerar boletim inteligente');
    } finally {
      setLoading(false);
    }
  };
  
  const getUrgencyColor = (level) => {
    const colors = {
      baixo: 'bg-green-500',
      medio: 'bg-yellow-500',
      alto: 'bg-red-500'
    };
    return colors[level] || 'bg-blue-500';
  };
  
  return (
    <div className="space-y-6" data-testid="boletim-inteligente">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-3 rounded-xl bg-gradient-to-br from-purple-600 to-pink-500">
            <Brain className="w-6 h-6 text-white" />
          </div>
          <div>
            <h3 className="text-xl font-bold text-white">Boletim Inteligente</h3>
            <p className="text-purple-300/70 text-sm">{studentName}</p>
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          <Select value={trimester} onValueChange={setTrimester}>
            <SelectTrigger className="w-[150px] bg-slate-700/50 border-purple-500/20 text-purple-200">
              <SelectValue placeholder="Trimestre" />
            </SelectTrigger>
            <SelectContent className="bg-slate-800 border-purple-500/20">
              <SelectItem value="1" className="text-purple-200">1º Trimestre</SelectItem>
              <SelectItem value="2" className="text-purple-200">2º Trimestre</SelectItem>
              <SelectItem value="3" className="text-purple-200">3º Trimestre</SelectItem>
              <SelectItem value="4" className="text-purple-200">4º Trimestre</SelectItem>
            </SelectContent>
          </Select>
          
          <Button
            onClick={generateBoletim}
            disabled={loading}
            className="bg-gradient-to-r from-purple-600 to-pink-500 hover:from-purple-700 hover:to-pink-600"
          >
            {loading ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Sparkles className="w-4 h-4 mr-2" />
            )}
            {loading ? 'Gerando...' : 'Gerar com IA'}
          </Button>
        </div>
      </div>
      
      {/* Loading State */}
      {loading && (
        <Card className="bg-slate-800/50 border-purple-500/20">
          <CardContent className="py-8">
            <div className="flex flex-col items-center gap-4">
              <div className="relative">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 animate-pulse" />
                <Brain className="w-8 h-8 text-white absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2" />
              </div>
              <p className="text-purple-300/70">Analisando dados do aluno...</p>
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Resultado */}
      {boletim && !loading && (
        <div className="space-y-4">
          {/* Análise Geral */}
          <Card className="bg-gradient-to-br from-purple-900/50 to-slate-800/50 border-purple-500/30">
            <CardContent className="py-6">
              <div className="flex items-start gap-4">
                <div className="p-2 rounded-lg bg-purple-500/20">
                  <FileText className="w-5 h-5 text-purple-400" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-white font-semibold">Análise Geral</h4>
                    <Badge className={`${getUrgencyColor(boletim.nivel_urgencia)} text-white`}>
                      Urgência: {boletim.nivel_urgencia}
                    </Badge>
                  </div>
                  <p className="text-purple-200/80">{boletim.analise_geral}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Pontos Fortes */}
            <Card className="bg-slate-800/50 border-green-500/30">
              <CardHeader className="pb-2">
                <CardTitle className="text-white flex items-center gap-2 text-base">
                  <Award className="w-4 h-4 text-green-400" />
                  Pontos Fortes
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  {(boletim.pontos_fortes || []).map((ponto, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                      <span className="text-purple-200 text-sm">{ponto}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
            
            {/* Áreas de Atenção */}
            <Card className="bg-slate-800/50 border-orange-500/30">
              <CardHeader className="pb-2">
                <CardTitle className="text-white flex items-center gap-2 text-base">
                  <AlertTriangle className="w-4 h-4 text-orange-400" />
                  Áreas de Atenção
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  {(boletim.areas_atencao || []).map((area, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <AlertTriangle className="w-4 h-4 text-orange-400 mt-0.5 flex-shrink-0" />
                      <span className="text-purple-200 text-sm">{area}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>
          
          {/* Recomendações */}
          <Card className="bg-slate-800/50 border-purple-500/20">
            <CardHeader className="pb-2">
              <CardTitle className="text-white flex items-center gap-2 text-base">
                <Lightbulb className="w-4 h-4 text-yellow-400" />
                Recomendações
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-3">
                {(boletim.recomendacoes || []).map((rec, idx) => (
                  <li key={idx} className="flex items-start gap-3 p-3 rounded-lg bg-slate-700/30">
                    <span className="w-6 h-6 rounded-full bg-purple-500 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                      {idx + 1}
                    </span>
                    <span className="text-purple-200 text-sm">{rec}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
          
          {/* Mensagem Motivacional */}
          {boletim.mensagem_motivacional && (
            <Card className="bg-gradient-to-r from-green-900/30 to-emerald-900/30 border-green-500/30">
              <CardContent className="py-4">
                <div className="flex items-center gap-3">
                  <Heart className="w-6 h-6 text-green-400" />
                  <p className="text-green-200 italic">"{boletim.mensagem_motivacional}"</p>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}
      
      {/* Estado Inicial */}
      {!boletim && !loading && (
        <Card className="bg-slate-800/50 border-purple-500/20">
          <CardContent className="py-12 text-center">
            <Brain className="w-16 h-16 text-purple-300/30 mx-auto mb-4" />
            <h4 className="text-white font-semibold mb-2">Gere um Boletim Inteligente</h4>
            <p className="text-purple-300/60 text-sm mb-4">
              Nossa IA irá analisar o desempenho do aluno e gerar insights personalizados
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ============================================
// BLOCO 5: SUGESTÃO DE ATIVIDADES
// ============================================

export function SugestaoAtividades({ studentId, studentName }) {
  const [loading, setLoading] = useState(false);
  const [sugestoes, setSugestoes] = useState(null);
  const [difficulty, setDifficulty] = useState('adequado');
  const [focusArea, setFocusArea] = useState('reforco');
  
  const generateSugestoes = async () => {
    setLoading(true);
    try {
      const res = await api.post('/dnacore/v2/ai/sugerir-atividades', {
        student_id: studentId,
        difficulty,
        focus_area: focusArea
      });
      
      setSugestoes(res.data.sugestoes);
      toast.success('Atividades sugeridas com sucesso!');
    } catch (error) {
      console.error('Erro ao gerar sugestões:', error);
      toast.error('Erro ao gerar sugestões de atividades');
    } finally {
      setLoading(false);
    }
  };
  
  const getTipoIcon = (tipo) => {
    const icons = {
      exercicio: BookOpen,
      projeto: Rocket,
      leitura: FileText,
      jogo: Zap,
      pesquisa: Target
    };
    const Icon = icons[tipo] || BookOpen;
    return <Icon className="w-4 h-4" />;
  };
  
  const getTipoColor = (tipo) => {
    const colors = {
      exercicio: 'bg-blue-500',
      projeto: 'bg-purple-500',
      leitura: 'bg-green-500',
      jogo: 'bg-yellow-500',
      pesquisa: 'bg-pink-500'
    };
    return colors[tipo] || 'bg-slate-500';
  };
  
  return (
    <div className="space-y-6" data-testid="sugestao-atividades">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-3">
          <div className="p-3 rounded-xl bg-gradient-to-br from-green-600 to-emerald-500">
            <Lightbulb className="w-6 h-6 text-white" />
          </div>
          <div>
            <h3 className="text-xl font-bold text-white">Sugestão de Atividades</h3>
            <p className="text-purple-300/70 text-sm">{studentName}</p>
          </div>
        </div>
        
        <div className="flex items-center gap-3 flex-wrap">
          <Select value={difficulty} onValueChange={setDifficulty}>
            <SelectTrigger className="w-[140px] bg-slate-700/50 border-purple-500/20 text-purple-200">
              <SelectValue placeholder="Dificuldade" />
            </SelectTrigger>
            <SelectContent className="bg-slate-800 border-purple-500/20">
              <SelectItem value="facil" className="text-purple-200">Fácil</SelectItem>
              <SelectItem value="adequado" className="text-purple-200">Adequado</SelectItem>
              <SelectItem value="desafiador" className="text-purple-200">Desafiador</SelectItem>
            </SelectContent>
          </Select>
          
          <Select value={focusArea} onValueChange={setFocusArea}>
            <SelectTrigger className="w-[150px] bg-slate-700/50 border-purple-500/20 text-purple-200">
              <SelectValue placeholder="Foco" />
            </SelectTrigger>
            <SelectContent className="bg-slate-800 border-purple-500/20">
              <SelectItem value="recuperacao" className="text-purple-200">Recuperação</SelectItem>
              <SelectItem value="reforco" className="text-purple-200">Reforço</SelectItem>
              <SelectItem value="aprofundamento" className="text-purple-200">Aprofundamento</SelectItem>
            </SelectContent>
          </Select>
          
          <Button
            onClick={generateSugestoes}
            disabled={loading}
            className="bg-gradient-to-r from-green-600 to-emerald-500 hover:from-green-700 hover:to-emerald-600"
          >
            {loading ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Sparkles className="w-4 h-4 mr-2" />
            )}
            {loading ? 'Gerando...' : 'Sugerir Atividades'}
          </Button>
        </div>
      </div>
      
      {/* Loading */}
      {loading && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[1, 2, 3, 4].map(i => (
            <Skeleton key={i} className="h-40 bg-slate-700" />
          ))}
        </div>
      )}
      
      {/* Resultado */}
      {sugestoes && !loading && (
        <div className="space-y-4">
          {/* Justificativa */}
          {sugestoes.justificativa && (
            <Card className="bg-slate-800/50 border-purple-500/20">
              <CardContent className="py-4">
                <div className="flex items-start gap-3">
                  <Brain className="w-5 h-5 text-purple-400 mt-0.5" />
                  <p className="text-purple-200">{sugestoes.justificativa}</p>
                </div>
              </CardContent>
            </Card>
          )}
          
          {/* Atividades */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {(sugestoes.atividades || []).map((atividade, idx) => (
              <Card key={idx} className="bg-slate-800/50 border-purple-500/20 hover:border-purple-500/40 transition-all">
                <CardContent className="py-4">
                  <div className="flex items-start gap-3">
                    <div className={`p-2 rounded-lg ${getTipoColor(atividade.tipo)}`}>
                      {getTipoIcon(atividade.tipo)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="text-white font-semibold truncate">{atividade.titulo}</h4>
                        <Badge className="bg-purple-500/20 text-purple-300 text-xs">
                          {atividade.tipo}
                        </Badge>
                      </div>
                      <p className="text-purple-300/50 text-xs mb-2">{atividade.disciplina}</p>
                      <p className="text-purple-200 text-sm mb-3">{atividade.descricao}</p>
                      
                      <div className="flex items-center gap-4 text-xs text-purple-300/70">
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {atividade.duracao_estimada || atividade.duracao}
                        </span>
                        <span className="flex items-center gap-1">
                          <Target className="w-3 h-3" />
                          {atividade.objetivo_pedagogico?.substring(0, 50) || atividade.objetivo?.substring(0, 50)}...
                        </span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          
          {/* Dica para Pais */}
          {sugestoes.dica_para_pais && (
            <Card className="bg-gradient-to-r from-blue-900/30 to-indigo-900/30 border-blue-500/30">
              <CardContent className="py-4">
                <div className="flex items-center gap-3">
                  <Heart className="w-5 h-5 text-blue-400" />
                  <div>
                    <p className="text-blue-300 font-medium text-sm">Dica para os Pais</p>
                    <p className="text-blue-200/80 text-sm">{sugestoes.dica_para_pais}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}
      
      {/* Estado Inicial */}
      {!sugestoes && !loading && (
        <Card className="bg-slate-800/50 border-purple-500/20">
          <CardContent className="py-12 text-center">
            <Lightbulb className="w-16 h-16 text-purple-300/30 mx-auto mb-4" />
            <h4 className="text-white font-semibold mb-2">Sugestões Personalizadas</h4>
            <p className="text-purple-300/60 text-sm">
              Configure as opções acima e clique em "Sugerir Atividades" para receber
              recomendações de atividades baseadas no perfil do aluno
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ============================================
// BLOCO 9: PLANO DE AÇÃO
// ============================================

export function PlanoAcao({ studentId, studentName }) {
  const [loading, setLoading] = useState(false);
  const [plano, setPlano] = useState(null);
  const [objetivo, setObjetivo] = useState('');
  const [prazoDias, setPrazoDias] = useState('30');
  
  const generatePlano = async () => {
    if (!objetivo.trim()) {
      toast.error('Digite um objetivo para o plano');
      return;
    }
    
    setLoading(true);
    try {
      const res = await api.post('/dnacore/v2/ai/gerar-plano-acao', {
        student_id: studentId,
        objetivo,
        prazo_dias: parseInt(prazoDias)
      });
      
      setPlano(res.data.plano);
      toast.success('Plano de ação gerado!');
    } catch (error) {
      console.error('Erro ao gerar plano:', error);
      toast.error('Erro ao gerar plano de ação');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div className="space-y-6" data-testid="plano-acao">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="p-3 rounded-xl bg-gradient-to-br from-orange-600 to-red-500">
          <ClipboardList className="w-6 h-6 text-white" />
        </div>
        <div>
          <h3 className="text-xl font-bold text-white">Plano de Ação</h3>
          <p className="text-purple-300/70 text-sm">{studentName}</p>
        </div>
      </div>
      
      {/* Formulário */}
      <Card className="bg-slate-800/50 border-purple-500/20">
        <CardContent className="py-4 space-y-4">
          <div>
            <label className="text-purple-200 text-sm font-medium mb-2 block">
              Objetivo do Plano
            </label>
            <Textarea
              value={objetivo}
              onChange={(e) => setObjetivo(e.target.value)}
              placeholder="Ex: Melhorar o desempenho em matemática, aumentar a frequência escolar..."
              className="bg-slate-700/50 border-purple-500/20 text-white placeholder:text-purple-300/40"
              rows={3}
            />
          </div>
          
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <label className="text-purple-200 text-sm font-medium mb-2 block">
                Prazo (dias)
              </label>
              <Select value={prazoDias} onValueChange={setPrazoDias}>
                <SelectTrigger className="bg-slate-700/50 border-purple-500/20 text-purple-200">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-purple-500/20">
                  <SelectItem value="15" className="text-purple-200">15 dias</SelectItem>
                  <SelectItem value="30" className="text-purple-200">30 dias</SelectItem>
                  <SelectItem value="45" className="text-purple-200">45 dias</SelectItem>
                  <SelectItem value="60" className="text-purple-200">60 dias</SelectItem>
                  <SelectItem value="90" className="text-purple-200">90 dias</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <Button
              onClick={generatePlano}
              disabled={loading || !objetivo.trim()}
              className="bg-gradient-to-r from-orange-600 to-red-500 hover:from-orange-700 hover:to-red-600 mt-6"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Sparkles className="w-4 h-4 mr-2" />
              )}
              Gerar Plano
            </Button>
          </div>
        </CardContent>
      </Card>
      
      {/* Resultado */}
      {plano && !loading && (
        <div className="space-y-4">
          {/* Título e Resumo */}
          <Card className="bg-gradient-to-br from-orange-900/30 to-slate-800/50 border-orange-500/30">
            <CardContent className="py-6">
              <h4 className="text-xl font-bold text-white mb-2">{plano.titulo}</h4>
              <p className="text-purple-200/80">{plano.resumo}</p>
              
              {plano.meta_principal && (
                <div className="mt-4 p-3 rounded-lg bg-orange-500/10 border border-orange-500/20">
                  <div className="flex items-center gap-2">
                    <Target className="w-4 h-4 text-orange-400" />
                    <span className="text-orange-200 font-medium">Meta: {plano.meta_principal}</span>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
          
          {/* Etapas */}
          <Card className="bg-slate-800/50 border-purple-500/20">
            <CardHeader className="pb-2">
              <CardTitle className="text-white flex items-center gap-2 text-base">
                <ClipboardList className="w-4 h-4 text-purple-400" />
                Etapas do Plano
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {(plano.etapas || []).map((etapa, idx) => (
                  <div key={idx} className="relative pl-8 pb-4 border-l-2 border-purple-500/30 last:border-0">
                    <div className="absolute left-[-9px] top-0 w-4 h-4 rounded-full bg-purple-500 border-4 border-slate-800" />
                    <div className="p-4 rounded-lg bg-slate-700/30">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-purple-300 font-medium">Semana {etapa.semana}</span>
                        <Badge className="bg-purple-500/20 text-purple-300 text-xs">
                          {etapa.responsavel}
                        </Badge>
                      </div>
                      <ul className="space-y-1">
                        {(etapa.acoes || []).map((acao, i) => (
                          <li key={i} className="flex items-start gap-2">
                            <CheckCircle className="w-4 h-4 text-purple-400 mt-0.5 flex-shrink-0" />
                            <span className="text-purple-200 text-sm">{acao}</span>
                          </li>
                        ))}
                      </ul>
                      {etapa.indicador_sucesso && (
                        <p className="text-purple-300/50 text-xs mt-2 italic">
                          Indicador: {etapa.indicador_sucesso}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
          
          {/* Critérios de Sucesso */}
          {plano.criterios_sucesso && (
            <Card className="bg-green-900/20 border-green-500/30">
              <CardContent className="py-4">
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-400 mt-0.5" />
                  <div>
                    <p className="text-green-300 font-medium text-sm">Critérios de Sucesso</p>
                    <p className="text-green-200/80 text-sm">{plano.criterios_sucesso}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}

export default { BoletimInteligente, SugestaoAtividades, PlanoAcao };
